/**
 * Normalize a proposed action coming from the copilot backend into the
 * frontend {@link CopilotAction} shape.
 *
 * The backend serializes an action's identifier under `id` (its DB primary
 * key — see ai-orchestrator `_action_to_dict`), while the widget keys, confirms
 * and rejects actions by `action_id`. Without this bridge a pending action's
 * `action_id` is `undefined`, so with several pending confirmation cards every
 * Confirmer/Rejeter click resolves to the FIRST card (and React logs duplicate
 * `undefined` keys). Accept either field so the mapping is always 1:1.
 */
import type { CopilotAction } from './types';

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `cp-act-${crypto.randomUUID()}`;
  }
  return `cp-act-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function normalizeAction(raw: unknown): CopilotAction {
  const a = (raw ?? {}) as Partial<CopilotAction> & { id?: string };
  const action_id =
    (typeof a.action_id === 'string' && a.action_id) ||
    (typeof a.id === 'string' && a.id) ||
    randomId();
  return { ...(a as CopilotAction), action_id };
}

export function normalizeActions(raw: unknown): CopilotAction[] {
  return Array.isArray(raw) ? raw.map(normalizeAction) : [];
}
